import React from 'react';
import { ListChecksIcon, MailIcon, PencilLineIcon, PlusIcon, XIcon } from 'lucide-react';
import { daysPastEta, getEscalation, isResolved } from '../../utils/conditions';
import type { AttentionFilter, Condition } from '../../types/conditions';
interface ConditionsHeaderProps {
  conditions: Condition[];
  attentionFilter: AttentionFilter;
  onAttentionFilterChange: (filter: AttentionFilter) => void;
  selectedCount: number;
  onSendConditions: () => void;
}
const outlineBtn = 'inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-charcoal transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';
export function ConditionsHeader({
  conditions,
  attentionFilter,
  onAttentionFilterChange,
  selectedCount,
  onSendConditions
}: ConditionsHeaderProps) {
  const total = conditions.length;
  const cleared = conditions.filter(c => isResolved(c.status)).length;
  const outstanding = total - cleared;
  const pct = total ? Math.round(cleared / total * 100) : 0;
  const overdue = conditions.filter(c => daysPastEta(c) > 0).length;
  const needEta = conditions.filter(c => getEscalation(c) !== 'none').length;
  return <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-6 py-5" data-id="element-161" data-mp="3jfz5h6u1w1tx">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3" data-id="element-162" data-mp="3od7pze2o8hyc">
        <Stat label="Outstanding" value={outstanding} data-id="element-163" data-mp="39h9s9rd3n5k0" />
        <Stat label="Cleared" value={cleared} data-id="element-164" data-mp="3egwbchflzg0q" />
        <div className="flex items-center gap-3" data-id="element-165" data-mp="3eipfnq2r1o5k">
          <ProgressRing pct={pct} data-id="element-166" data-mp="30hmt7c7uebu4" />
          <Stat label="Complete" value={`${pct}%`} data-id="element-167" data-mp="30iofkcub6kr5" />
        </div>

        <span className="hidden h-10 w-px bg-slate-200 sm:block" aria-hidden="true" data-id="element-168" data-mp="35fy1vjmeigar" />

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by attention needed" data-id="element-169" data-mp="yq9pgqhm29zn">
          <FilterChip count={overdue} label="past ETA" active={attentionFilter === 'overdue'} tone="red" onClick={() => onAttentionFilterChange(attentionFilter === 'overdue' ? 'all' : 'overdue')} data-id="element-170" data-mp="13pmlo1udgtew" />
          <FilterChip count={needEta} label="need ETA" active={attentionFilter === 'escalated'} tone="amber" onClick={() => onAttentionFilterChange(attentionFilter === 'escalated' ? 'all' : 'escalated')} data-id="element-171" data-mp="xb7akocq3mzf" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-id="element-172" data-mp="1l08cdsr1vzvq">
        {selectedCount > 0 && <button type="button" className={outlineBtn} data-id="element-173" data-mp="1g1opnq4muk2b">
            <PencilLineIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-174" data-mp="1b7kkncexgm3v" />
            Mass update ({selectedCount})
          </button>}
        <button type="button" className={outlineBtn} data-id="element-175" data-mp="23owts4qbrn2r">
          <ListChecksIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-176" data-mp="23poo7c86rerg" />
          Create tasks
        </button>
        <button type="button" className={outlineBtn} data-id="element-177" data-mp="1yw3ptj2okirq">
          <PlusIcon className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-178" data-mp="26a4odp8fu8m5" />
          Add condition
        </button>
        <button type="button" onClick={onSendConditions} className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-id="element-179" data-mp="2b97fs6vhwgbn">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-180" data-mp="2p64gr15bve69" />
          Send conditions
        </button>
      </div>
    </header>;
}
function Stat({
  label,
  value
}: {
  label: string;
  value: number | string;
}) {
  return <div data-id="element-181" data-mp="33wtv2ce359ec">
      <p className="text-xs text-slate-500" data-id="element-182" data-mp="33xmu0r6syulx">{label}</p>
      <p className="text-2xl font-medium tabular-nums leading-tight text-charcoal" data-id="element-183" data-mp="33wbsyz4qs0yi">{value}</p>
    </div>;
}
function ProgressRing({
  pct
}: {
  pct: number;
}) {
  const r = 15;
  const c = 2 * Math.PI * r;
  return <svg width="38" height="38" viewBox="0 0 38 38" role="img" aria-label={`${pct}% complete`} className="-rotate-90" data-id="element-184" data-mp="3cvf7dpsyrs6o">
      <circle cx="19" cy="19" r={r} fill="none" strokeWidth="4" className="stroke-slate-100" data-id="element-185" data-mp="9ifbujtdt7b4" />
      <circle cx="19" cy="19" r={r} fill="none" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} className="stroke-steel transition-[stroke-dashoffset] duration-500" data-id="element-186" data-mp="n9c6eg6vbykt" />
    </svg>;
}
interface FilterChipProps {
  count: number;
  label: string;
  active: boolean;
  tone: 'red' | 'amber';
  onClick: () => void;
}
function FilterChip({
  count,
  label,
  active,
  tone,
  onClick
}: FilterChipProps) {
  if (count === 0 && !active) return null;
  const styles = tone === 'red' ? {
    chip: 'bg-red-50 text-red-600 hover:bg-red-100',
    ring: 'ring-red-400',
    dot: 'bg-red-500'
  } : {
    chip: 'bg-amber-50 text-amber-700 hover:bg-amber-100',
    ring: 'ring-amber-400',
    dot: 'bg-amber-500'
  };
  return <button type="button" aria-pressed={active} onClick={onClick} title={active ? 'Clear filter' : `Show only conditions ${label}`} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${styles.chip} ${active ? `ring-2 ring-inset ${styles.ring}` : ''}`} data-id="element-187" data-mp="1jf41yqqwoxte">
      <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} aria-hidden="true" data-id="element-188" data-mp="2276m4c2raw8e" />
      {count} {label}
      {active && <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-189" data-mp="277u9mnrs0uxv" />}
    </button>;
}